import { getAccessToken } from "@vv/api";
import { io, type Socket } from "socket.io-client";
import type { AppNotification } from "../domain/notification.types";

let socket: Socket | null = null;

/**
 * `apiBaseUrl` is the REST base (e.g. "/api" in production, same origin as the
 * page - see cloudfront.tf's path-based routing) or an absolute
 * "http://host:port/api" in local dev. Socket.IO's own default path
 * ("/socket.io") and default namespace ("/") live at the *origin*, not under
 * "/api" - connecting `io(apiBaseUrl)` directly would ask Socket.IO for a
 * namespace literally called "/api" instead. Strip any path, keeping only the
 * scheme+host (or nothing, for a same-origin connection from a relative
 * apiBaseUrl - `io()` with an empty string already means "current origin").
 */
function toSocketOrigin(apiBaseUrl: string): string {
  if (!/^https?:\/\//.test(apiBaseUrl)) return "";
  return new URL(apiBaseUrl).origin;
}

/**
 * One shared connection for the whole app, authenticated with the same JWT
 * access token used for REST calls (see `NotificationsGateway.handleConnection`
 * on the backend). Safe to call more than once - a second call while already
 * connected is a no-op.
 */
export function connectNotificationsSocket(
  apiBaseUrl: string,
  onNotification: (notification: AppNotification) => void,
): void {
  if (socket) return;
  const token = getAccessToken();
  if (!token) return;

  socket = io(toSocketOrigin(apiBaseUrl), {
    auth: { token },
    transports: ["websocket"],
  });
  socket.on("notification:new", onNotification);
}

export function disconnectNotificationsSocket(): void {
  socket?.disconnect();
  socket = null;
}
